import React from 'react';
import { useNavigate } from 'react-router-dom';
import useGlobalSearch from '@/hooks/useGlobalSearch';
import { useClients } from '@/hooks/useData';
import { useWorkspace } from '@/contexts/WorkspaceContext';

const STATUS_LABEL = {
  draft: 'Koncept',
  issued: 'Vystavená',
  paid: 'Zaplatená',
  overdue: 'Po splatnosti',
  cancelled: 'Zrušená',
};

const CATEGORY_LABEL = {
  food: 'Strava',
  fuel: 'Pohonné hmoty',
  software: 'Softvér',
  hardware: 'Hardvér',
  marketing: 'Marketing',
  office: 'Kancelária',
  services: 'Služby',
  other: 'Ostatné',
};

function formatMoney(n) {
  const v = Number(n) || 0;
  return new Intl.NumberFormat('sk-SK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v) + ' €';
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
}

export default function GlobalSearchResults({ query, onPick, variant = 'panel' }) {
  const navigate = useNavigate();
  const { workspace } = useWorkspace();
  const { invoices, clients, expenses, loading, error } = useGlobalSearch(query);
  const { clients: allClients } = useClients(workspace?.id);

  const clientById = React.useMemo(() => {
    const map = new Map();
    (allClients || []).forEach((c) => map.set(c.id, c));
    return map;
  }, [allClients]);

  if (!query || query.trim().length < 2) return null;

  const total = invoices.length + clients.length + expenses.length;
  const empty = !loading && total === 0;

  const go = (path) => {
    navigate(path);
    onPick?.();
  };

  return (
    <div
      className="cd-global-search-results"
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-md)',
        boxShadow: variant === 'popover' ? 'var(--sh-md)' : 'var(--sh-sm)',
        padding: 4,
        maxHeight: '60vh',
        overflowY: 'auto',
      }}
    >
      {error && (
        <div style={{ padding: '10px 12px', fontSize: 12.5, color: 'var(--danger, #d93b3b)' }}>
          {error}
        </div>
      )}

      {loading && total === 0 && (
        <div style={{ padding: '10px 12px', fontSize: 12.5, color: 'var(--text-muted)' }}>
          Hľadám…
        </div>
      )}

      {empty && (
        <div style={{ padding: '14px 12px', fontSize: 13, color: 'var(--text-muted)' }}>
          Žiadne výsledky pre „{query}".
        </div>
      )}

      {clients.length > 0 && (
        <Group label="Klienti">
          {clients.map((c) => (
            <Row
              key={`c-${c.id}`}
              onClick={() => go('/klienti')}
              title={c.name || 'Bez mena'}
              subtitle={[c.ico && `IČO ${c.ico}`, c.email].filter(Boolean).join(' · ')}
              right={null}
            />
          ))}
        </Group>
      )}

      {invoices.length > 0 && (
        <Group label="Faktúry">
          {invoices.map((inv) => {
            const client = inv.client_id ? clientById.get(inv.client_id) : null;
            return (
              <Row
                key={`i-${inv.id}`}
                onClick={() => go('/faktury')}
                title={inv.number || 'FA bez čísla'}
                mono
                subtitle={[
                  client?.name,
                  STATUS_LABEL[inv.status] || inv.status,
                  formatDate(inv.issue_date || inv.due_date),
                ].filter(Boolean).join(' · ')}
                right={inv.total != null ? formatMoney(inv.total) : null}
              />
            );
          })}
        </Group>
      )}

      {expenses.length > 0 && (
        <Group label="Výdavky">
          {expenses.map((e) => (
            <Row
              key={`e-${e.id}`}
              onClick={() => go('/vydavky')}
              title={e.vendor_name || e.description || 'Bez dodávateľa'}
              subtitle={[
                CATEGORY_LABEL[e.category] || e.category,
                formatDate(e.date),
                e.description && e.vendor_name ? e.description : null,
              ].filter(Boolean).join(' · ')}
              right={e.amount != null ? formatMoney(e.amount) : null}
            />
          ))}
        </Group>
      )}
    </div>
  );
}

function Group({ label, children }) {
  return (
    <div>
      <div style={{
        fontSize: 10,
        fontWeight: 700,
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.09em',
        padding: '8px 10px 4px',
      }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function Row({ title, subtitle, right, mono, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        padding: '8px 10px',
        border: 'none',
        background: 'transparent',
        borderRadius: 'var(--r-sm)',
        cursor: 'pointer',
        textAlign: 'left',
        font: 'inherit',
        color: 'inherit',
        transition: 'background var(--t-fast) var(--ease)',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          className={mono ? 'mono' : undefined}
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--text-primary)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </div>
        {subtitle && (
          <div style={{
            fontSize: 11.5,
            color: 'var(--text-muted)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            marginTop: 2,
          }}>
            {subtitle}
          </div>
        )}
      </div>
      {right && (
        <span
          className="mono"
          style={{
            fontSize: 12.5,
            fontWeight: 600,
            color: 'var(--text-secondary)',
            flexShrink: 0,
          }}
        >
          {right}
        </span>
      )}
    </button>
  );
}
